/* eslint-disable import/no-duplicates */
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState, useCallback } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import {
  FiAlertOctagon,
  FiCheck,
  FiClock,
  FiUser,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import { FormButton } from "../../../components/FormButton";
import { Input } from "../../../components/Input";
import { SelectSingle } from "../../../components/SelectSingle";
import { AuthContext } from "../../../contexts/AuthContext";
import { api } from "../../../services/api";
import { PageTitle } from "../../../components/PageTitle";
import { Modal, ModalButton } from "../../../components/Modal";
import { InputMask } from "../../../components/InputMask";
import { maskZIP } from "../../../components/masks/Masks";

interface Costumer {
  name: string;
  zip: string;
  address: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  alert_days_without_report: number;
  zone_id?: string;
  zone?: {
    id: string;
    name: string;
  };
  latitude?: number | null;
  longitude?: number | null;
  is_delivery_only?: boolean;
}

interface CostumerFormValues {
  name: string;
  zip: string;
  address: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  alert_days_without_report: number;
  zone_id: string;
  coordinates: string;
  is_delivery_only: boolean | string;
}

interface ResponseViaCep {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
}

interface Zone {
  id: string;
  name: string;
  status: string;
}

interface Observation {
  id: string;
  text: string;
  status: "open" | "closed";
  opened_at: string;
  closed_at: string | null;
  user: {
    id: string;
    name: string;
  };
}

const schema = Yup.object().shape({
  name: Yup.string().required("Campo cliente é obrigatório"),
  zip: Yup.string().required("Campo CEP é obrigatório"),
  address: Yup.string().required("Campo logradouro é obrigatório"),
  number: Yup.string().required("Campo número é obrigatório"),
  district: Yup.string().required("Campo bairro é obrigatório"),
  city: Yup.string().required("Campo cidade é obrigatório"),
  state: Yup.string().required("Campo estado é obrigatório"),
});

export function CostumerEdit() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [costumer, setCostumer] = useState<Costumer>();
  const [zones, setZones] = useState<Zone[]>([]);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [showObservations, setShowObservations] = useState(true);
  const [confirmCloseModal, setConfirmCloseModal] = useState<{
    isOpen: boolean;
    observationId: string;
  }>({ isOpen: false, observationId: "" });
  const [closingObservation, setClosingObservation] = useState(false);
  const navigate = useNavigate();

  // Função para fechar o modal de confirmação
  const closeConfirmModal = useCallback(() => {
    setConfirmCloseModal({ isOpen: false, observationId: "" });
  }, []);

  // Carrega observações do cliente
  const loadObservations = useCallback(async () => {
    if (!id) return;
    setLoadingObservations(true);
    try {
      const response = await api.get(`/costumer-observations/${id}`);
      setObservations(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erro ao carregar observações:", error);
    } finally {
      setLoadingObservations(false);
    }
  }, [id]);

  useEffect(() => {
    // Carregar zonas
    api.get("/zones").then(response => {
      setZones(response.data);
    });

    if (id) {
      api.get(`costumers/${id}`).then(response => {
        return setCostumer(response.data);
      });
      loadObservations();
    }
  }, [id, loadObservations]);

  function handleZip(zip: string, setFieldValue) {
    const cleanZip = zip?.replace(/\D/g, "");
    if (cleanZip?.length !== 8) {
      return null;
    }

    axios
      .get<ResponseViaCep>(`https://viacep.com.br/ws/${cleanZip}/json/`)
      .then(response => {
        setFieldValue("address", response.data.logradouro);
        setFieldValue("district", response.data.bairro);
        setFieldValue("city", response.data.localidade);
        setFieldValue("state", response.data.uf);
      });

    return null;
  }

  function handleSubmit(data: CostumerFormValues) {
    // Remove máscara do CEP antes de enviar
    // Parseia coordenadas do campo único (formato: "-23.123, -46.456")
    let latitude: number | null = null;
    let longitude: number | null = null;

    if (data.coordinates && data.coordinates.trim()) {
      const parts = data.coordinates.split(",").map(p => p.trim());
      if (parts.length === 2) {
        const latValue = parseFloat(parts[0]);
        const lonValue = parseFloat(parts[1]);
        if (!Number.isNaN(latValue) && !Number.isNaN(lonValue)) {
          latitude = latValue;
          longitude = lonValue;
        }
      }
    }

    const dataToSend = {
      ...data,
      zip: data.zip?.replace(/\D/g, "") || "",
      zone_id: data.zone_id || null, // Converte string vazia para null
      latitude,
      longitude,
      is_delivery_only: data.is_delivery_only === true || data.is_delivery_only === "true",
    };

    // Remove o campo coordinates que não existe no backend
    delete (dataToSend as any).coordinates;

    if (id) {
      api.put(`/costumers/${id}`, dataToSend).then(() => {
        enqueueSnackbar("Cliente editado com sucesso!", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
      });
    } else {
      api.post("/costumers", dataToSend).then(() => {
        enqueueSnackbar("Cliente cadastrado com sucesso!", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
      });
    }
    return navigate("/costumers");
  }

  // Abre modal de confirmação para fechar observação
  function openCloseConfirmation(observationId: string) {
    setConfirmCloseModal({ isOpen: true, observationId });
  }

  // Fecha uma observação
  const handleCloseObservation = useCallback(async () => {
    if (!confirmCloseModal.observationId) return;
    setClosingObservation(true);
    try {
      await api.patch("/costumer-observations/close", {
        observation_ids: [confirmCloseModal.observationId],
        costumer_id: id,
      });
      enqueueSnackbar("Observação fechada com sucesso!", {
        variant: "success",
      });
      setConfirmCloseModal({ isOpen: false, observationId: "" });
      loadObservations();
      // Recarrega o cliente para atualizar status de emergência
      if (id) {
        const response = await api.get(`costumers/${id}`);
        setCostumer(response.data);
      }
    } catch (error) {
      enqueueSnackbar("Erro ao fechar observação", {
        variant: "error",
      });
    } finally {
      setClosingObservation(false);
    }
  }, [confirmCloseModal.observationId, id, loadObservations]);

  // Formata data
  function formatDate(dateString: string): string {
    return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", {
      locale: ptBR,
    });
  }

  // Verifica se é a última observação aberta
  const isLastOpenObservation =
    observations.filter(obs => obs.status === "open").length === 1;

  const initialValues = {
    name: costumer ? costumer.name : "",
    zip: costumer ? costumer.zip : "",
    address: costumer ? costumer.address : "",
    number: costumer ? costumer.number : "",
    complement: costumer ? costumer.complement : "",
    district: costumer ? costumer.district : "",
    city: costumer ? costumer.city : "",
    state: costumer ? costumer.state : "",
    alert_days_without_report: costumer
      ? costumer.alert_days_without_report
      : 0,
    is_delivery_only: costumer?.is_delivery_only ?? false,
    zone_id: costumer ? costumer.zone_id || "" : "",
    coordinates: costumer?.latitude && costumer?.longitude
      ? `${costumer.latitude}, ${costumer.longitude}`
      : "",
  };

  return (
    <div className="container mx-auto">
      <PageTitle
        title={id ? "Editar Cliente" : "Novo Cliente"}
        backButton
        backPath="/costumers"
      />
      <div className="w-full">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={schema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ errors, setFieldValue, values }) => (
              <Form className="space-y-4">
                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Cliente
                  </span>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Nome do cliente"
                    error={errors.name}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Alerta de dias sem relatório
                  </span>
                  <Input
                    type="number"
                    name="alert_days_without_report"
                    placeholder="Dias sem relatório"
                    error={errors.alert_days_without_report}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Zona
                  </span>
                  <SelectSingle name="zone_id" error={errors.zone_id}>
                    <option value="">Selecione uma zona</option>
                    {zones
                      .filter(zone => zone.status === "A")
                      .map(zone => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))}
                  </SelectSingle>
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Apenas entrega?
                  </span>
                  <Field
                    as="select"
                    name="is_delivery_only"
                    className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                    parse={(value: string) => value === "true"}
                  >
                    <option value="false">Não</option>
                    <option value="true">Sim</option>
                  </Field>
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    CEP
                  </span>
                  <InputMask
                    type="text"
                    name="zip"
                    placeholder="00000-000"
                    value={maskZIP(values.zip || "")}
                    onChange={e => {
                      const masked = maskZIP(e.target.value);
                      setFieldValue("zip", masked);
                    }}
                    onBlur={e => handleZip(e.target.value, setFieldValue)}
                    error={errors.zip}
                    maxLength={9}
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Logradouro
                  </span>

                  <Input
                    type="text"
                    name="address"
                    placeholder="Logradouro"
                    error={errors.address}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Número
                  </span>

                  <Input
                    type="text"
                    name="number"
                    placeholder="Número"
                    error={errors.number}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Complemento
                  </span>
                  <Input
                    type="text"
                    name="complement"
                    placeholder="Complemento"
                    error={errors.complement}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Bairro
                  </span>
                  <Input
                    type="text"
                    name="district"
                    placeholder="Bairro"
                    error={errors.district}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Cidade
                  </span>
                  <Input
                    type="text"
                    name="city"
                    placeholder="Cidade"
                    error={errors.city}
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Estado
                  </span>
                  <Field
                    as="select"
                    name="state"
                    className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                  >
                    <option value="">Selecione o estado</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                    <option value="EX">Estrangeiro</option>
                  </Field>
                </div>

                {/* Coordenadas Manuais */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <span className="text-sm text-[#0085BD] font-medium">
                    Coordenadas (opcional - preenchido automaticamente)
                  </span>
                  <div className="mt-2">
                    <Input
                      type="text"
                      name="coordinates"
                      placeholder="Ex: -23.550520, -46.633308"
                      className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Cole as coordenadas no formato: latitude, longitude (ex: -23.550520, -46.633308)
                  </p>
                </div>

                <FormButton
                  title={id ? "Editar Cliente" : "Cadastrar Cliente"}
                  type="submit"
                  className="w-full h-[52px] bg-[#0085BD] text-white rounded-lg hover:bg-[#006a96] transition-colors font-medium"
                />
              </Form>
            )}
          </Formik>
        </div>

        {/* Seção de Observações Emergenciais */}
        {id && (
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <button
              type="button"
              onClick={() => setShowObservations(!showObservations)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <FiAlertOctagon
                  className={
                    observations.some(o => o.status === "open")
                      ? "text-red-500"
                      : "text-gray-400"
                  }
                  size={24}
                />
                <h2 className="text-lg font-semibold text-[#0085BD]">
                  Observações Emergenciais
                </h2>
                {observations.filter(o => o.status === "open").length > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                    {observations.filter(o => o.status === "open").length}{" "}
                    aberta
                    {observations.filter(o => o.status === "open").length > 1
                      ? "s"
                      : ""}
                  </span>
                )}
              </div>
              {showObservations ? (
                <FiChevronUp className="text-gray-400" size={20} />
              ) : (
                <FiChevronDown className="text-gray-400" size={20} />
              )}
            </button>

            {showObservations && (
              <div className="mt-4">
                {loadingObservations && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0085BD]" />
                  </div>
                )}

                {!loadingObservations && observations.length === 0 && (
                  <p className="text-gray-400 text-center py-8">
                    Nenhuma observação emergencial registrada.
                  </p>
                )}

                {!loadingObservations && observations.length > 0 && (
                  <div className="space-y-3">
                    {observations.map(observation => (
                      <div
                        key={observation.id}
                        className={`p-4 rounded-lg border-l-4 ${
                          observation.status === "open"
                            ? "bg-red-50 border-red-500"
                            : "bg-gray-50 border-gray-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p
                              className={`text-sm ${
                                observation.status === "open"
                                  ? "text-gray-800"
                                  : "text-gray-500"
                              }`}
                            >
                              {observation.text}
                            </p>
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <FiUser size={12} />
                                {observation.user?.name || "Usuário"}
                              </span>
                              <span className="flex items-center gap-1">
                                <FiClock size={12} />
                                Aberta em {formatDate(observation.opened_at)}
                              </span>
                              {observation.closed_at && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <FiCheck size={12} />
                                  Fechada em {formatDate(observation.closed_at)}
                                </span>
                              )}
                            </div>
                          </div>
                          {observation.status === "open" && (
                            <button
                              type="button"
                              onClick={() =>
                                openCloseConfirmation(observation.id)
                              }
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors"
                            >
                              <FiCheck size={14} />
                              Fechar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmação para fechar observação */}
      <Modal
        isOpen={confirmCloseModal.isOpen}
        onClose={closeConfirmModal}
        title="Fechar Observação Emergencial"
        icon={<FiAlertOctagon size={24} />}
        variant="warning"
        footer={
          <>
            <ModalButton variant="cancel" onClick={closeConfirmModal}>
              Cancelar
            </ModalButton>
            <ModalButton
              variant="success"
              onClick={handleCloseObservation}
              disabled={closingObservation}
            >
              {closingObservation ? "Fechando..." : "Confirmar"}
            </ModalButton>
          </>
        }
      >
        <p className="text-gray-700">
          Tem certeza que deseja fechar esta observação emergencial?
        </p>
        {isLastOpenObservation && (
          <p className="mt-3 text-amber-600 font-medium">
            <FiAlertOctagon className="inline mr-1" />
            Esta é a última observação aberta. O cliente deixará de ser marcado
            como emergência.
          </p>
        )}
      </Modal>
    </div>
  );
}
